import type * as React from 'react';

import { DailyGoalTypeNames, ProgressStatus, WEEKLY_STREAKS_DEFAULT_TARGET_DAYS } from 'bundles/daily-goals/constants';
import type {
  DailyGoal,
  DailyGoalProgressCompleteItems,
  DailyGoalProgressData,
  DailyGoalProgressMessage,
  DailyGoalTypeNameString,
  LearningAssistantDailyGoalMessage,
  WeeklyStreakCelebrationData,
  WeeklyStreakProgressToastData,
} from 'bundles/daily-goals/types';
import { isCompleteItemsProgressDetails } from 'bundles/daily-goals/utils/dailyGoalsUtils';
import type { GetLearnerWeeklyGoals_WeeklyGoalFragment as LearnerGoals } from 'bundles/goal-setting/components/unified-goals/queries/__generated__/GetLearnerWeeklyGoals';
import { makeWeeklyStreaksImpression } from 'bundles/goal-setting/components/unified-goals/utils/featureGates';
import type { DaysOfTheWeek } from 'bundles/goal-setting/components/unified-goals/utils/goalProgressUtils';
import type { DayOfWeek } from 'bundles/goal-setting/utils/constants';
import { mapApiDayToMomentDay } from 'bundles/goal-setting/utils/constants';
import {
  COURSE_COMPLETION_MESSAGE_TYPE,
  MODULE_COMPLETION_MESSAGE_TYPE,
  WEEKLY_STREAK_PROGRESS_COMPLETION_MESSAGE_TYPE,
} from 'bundles/learning-assistant/constants/messageTypes';
import type {
  LearningAssistanceMessage,
  WeeklyStreakProgressCompletionMessageBody,
} from 'bundles/learning-assistant/types/RealtimeMessages';

// Map Learning Assistant message types to GraphQL types
const LA_MESSAGE_TYPE_TO_GRAPHQL_TYPE: Record<string, string> = {
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressCompleteItems':
    DailyGoalTypeNames.CompleteItems,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressCompleteReadings':
    DailyGoalTypeNames.CompleteReadings,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressCompleteLectures':
    DailyGoalTypeNames.CompleteLectures,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressCompleteHandsOnItems':
    DailyGoalTypeNames.CompleteHandsOnItems,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressCompleteAssessments':
    DailyGoalTypeNames.CompleteAssessments,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressCompletePractice':
    DailyGoalTypeNames.CompletePractice,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressCompleteModule':
    DailyGoalTypeNames.CompleteModule,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressScore80PercentOnAssignment':
    DailyGoalTypeNames.Score80PercentOnAssignment,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressUnlockPersonalizedTracker':
    DailyGoalTypeNames.UnlockPersonalizedTracker,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressAddCareerGoal':
    DailyGoalTypeNames.AddCareerGoal,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressPracticeWithCoach':
    DailyGoalTypeNames.PracticeWithCoach,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressReturnToCoursera':
    DailyGoalTypeNames.ReturnToCoursera,
  'org.coursera.learningassistance.messages.dailygoals.DailyGoalProgressSetUpLearningPlan':
    DailyGoalTypeNames.SetUpLearningPlan,
};

// Learning item daily goals - excludes non-learning goals like career goals, setup, etc.
export const learningItemDailyGoals = Object.keys(LA_MESSAGE_TYPE_TO_GRAPHQL_TYPE).filter((key) => {
  const value = LA_MESSAGE_TYPE_TO_GRAPHQL_TYPE[key];
  return (
    value !== DailyGoalTypeNames.SetUpLearningPlan &&
    value !== DailyGoalTypeNames.ReturnToCoursera &&
    value !== DailyGoalTypeNames.PracticeWithCoach &&
    value !== DailyGoalTypeNames.AddCareerGoal &&
    value !== DailyGoalTypeNames.UnlockPersonalizedTracker
  );
});

// Set for faster lookup
const learningItemDailyGoalsSet = new Set(learningItemDailyGoals);

/**
 * Check if a message type is a learning item daily goal
 * @param messageTypeId The full message type ID from Learning Assistant
 * @returns True if this is a learning item goal type
 */
export const isLearningItemDailyGoal = (messageTypeId: string): boolean => {
  return learningItemDailyGoalsSet.has(messageTypeId);
};

export const getGraphQLTypeFromLAMessageType = (messageType: string): string | undefined => {
  return LA_MESSAGE_TYPE_TO_GRAPHQL_TYPE[messageType];
};

/**
 * Map Learning Assistant progress status to GraphQL daily goal progress status
 * @param learningAssistantStatus Status from Learning Assistant message
 * @returns Corresponding GraphQL progress status
 */
export const mapProgressStatus = (learningAssistantStatus: 'COMPLETED' | 'ON_TRACK'): ProgressStatus => {
  switch (learningAssistantStatus) {
    case 'COMPLETED':
      return ProgressStatus.Completed;
    case 'ON_TRACK':
      return ProgressStatus.InProgress;
    default:
      return ProgressStatus.InProgress; // Default fallback
  }
};

/**
 * Extract goal type from Learning Assistant message type ID
 * @param messageTypeId Format: "DailyGoalProgress~2025-06-26!~COMPLETE_ITEMS"
 * @returns Goal type (e.g., "COMPLETE_ITEMS") or null if parsing fails
 */
export const extractGoalTypeFromMessageId = (messageTypeId: string): string | null => {
  const parts = messageTypeId.split('~');
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return parts.length >= 3 ? parts[2] : null;
};

/**
 * Find matching daily goal by goal type
 * @param goalType The goal type to match (e.g., "COMPLETE_ITEMS")
 * @param currentDailyGoals Array of current daily goals
 * @returns Index of matching goal or -1 if not found
 */
export const findIndexOfMatchingDailyGoal = (goalType: string, currentDailyGoals: Array<DailyGoal>): number => {
  return currentDailyGoals.findIndex((goal) => {
    // goalId format: "2025-06-27~COMPLETE_ITEMS"
    const goalParts = goal.goalId.split('~');
    const goalTypeFromId = goalParts.length >= 2 ? goalParts[1] : null;
    return goalTypeFromId === goalType;
  });
};

/**
 * Check if a message is a valid daily goal progress message
 * @param message The message to validate
 * @returns True if message is valid daily goal progress message
 */
export const isValidDailyGoalProgressMessage = (message: unknown): message is LearningAssistantDailyGoalMessage => {
  const msg = message as LearningAssistantDailyGoalMessage;
  return !!(
    msg?.messageBody?.messageBody?.typeName === 'dailyGoalProgressMessage' &&
    msg?.messageBody?.messageBody?.definition?.dailyGoals?.length > 0
  );
};

/**
 * Check if a message is any valid Learning Assistant message
 * @param message The message to validate
 * @returns True if message is a valid Learning Assistant message
 */
export const isValidLearningAssistantMessage = (message: unknown): message is LearningAssistanceMessage => {
  // Currently only supports daily goal messages, but extensible for future message types
  // Future: || isValidStreakMessage(message) || isValidBadgeMessage(message)
  return isValidDailyGoalProgressMessage(message);
};

/**
 * Convert API day format to goal-setting day format
 * @param apiDays Array of days in API format (e.g., ['MONDAY', 'TUESDAY'])
 * @returns Array of days in goal-setting format (e.g., ['mo', 'tu'])
 */
export const convertApiDaysToGoalSettingDays = (apiDays: DaysOfTheWeek[]): DayOfWeek[] => {
  const twoLetterDaysOfWeek = apiDays.map((day) => mapApiDayToMomentDay[day]);
  return twoLetterDaysOfWeek.filter(Boolean);
};

/**
 * Check if a message is a valid weekly streak progress completion message
 * @param message The message to validate
 * @returns True if message is valid weekly streak progress completion message
 */
export const isValidWeeklyStreakProgressCompletionMessage = (
  message: unknown
): message is LearningAssistanceMessage => {
  const msg = message as LearningAssistanceMessage;
  return !!(
    msg?.messageBody?.messageBody?.typeName === WEEKLY_STREAK_PROGRESS_COMPLETION_MESSAGE_TYPE &&
    typeof msg?.messageBody?.messageBody?.definition?.streakCount === 'number' &&
    Array.isArray(msg?.messageBody?.messageBody?.definition?.completedDays)
  );
};

/**
 * Extract target days array from learner goals data
 * @param learnerGoals Learner weekly goals data
 * @returns Array of target days in goal-setting format
 */
export const extractTargetDaysFromLearnerGoals = (learnerGoals: LearnerGoals | undefined | null): DayOfWeek[] => {
  return (
    learnerGoals?.dayGoalStatus
      ?.filter((dayGoal) => dayGoal.isGoalDay)
      .map((dayGoal) => mapApiDayToMomentDay[dayGoal.day])
      .filter(Boolean) || []
  );
};

/**
 * Determine if weekly target is complete based on target and completed days
 * @param targetDaysArray Target days for the week
 * @param completedDays Completed days from message
 * @returns True if weekly target is complete
 */
export const isWeeklyTargetComplete = (targetDaysArray: DayOfWeek[], completedDays: DaysOfTheWeek[]): boolean => {
  const targetDaysCount = targetDaysArray.length > 0 ? targetDaysArray.length : WEEKLY_STREAKS_DEFAULT_TARGET_DAYS;
  return completedDays.length >= targetDaysCount;
};

/**
 * Process weekly streak progress completion message into celebration UI format
 * @param message Learning Assistant message
 * @param setWeeklyStreakViewData Function to update weekly streak celebration data
 */
export const processWeeklyStreakProgressCompletionMessage = (
  message: LearningAssistanceMessage,
  setWeeklyStreakViewData: React.Dispatch<React.SetStateAction<WeeklyStreakCelebrationData | undefined>>
) => {
  // TypeScript type narrowing: we know this is a weekly streak message after validation
  const { definition } = message.messageBody.messageBody as WeeklyStreakProgressCompletionMessageBody;

  // Handle the case where targetDays might not be returned
  const targetDaysArray = definition.targetDays || [];

  const weeklyStreakData: WeeklyStreakCelebrationData = {
    streakCount: definition.streakCount,
    targetDays: convertApiDaysToGoalSettingDays(targetDaysArray),
    completedDays: convertApiDaysToGoalSettingDays(definition.completedDays),
    weeklyTarget: targetDaysArray.length > 0 ? targetDaysArray.length : WEEKLY_STREAKS_DEFAULT_TARGET_DAYS,
  };

  setWeeklyStreakViewData(weeklyStreakData);
};

/**
 * Process weekly streak progress message into progress toast format
 * @param message Learning Assistant message
 * @param learnerGoals Learner weekly goals data
 * @param setWeeklyStreakProgressToastData Function to update weekly streak progress toast data
 */
export const processWeeklyStreakProgressMessage = (
  message: LearningAssistanceMessage,
  learnerGoals: LearnerGoals | undefined | null,
  setWeeklyStreakProgressToastData: React.Dispatch<React.SetStateAction<WeeklyStreakProgressToastData | undefined>>
) => {
  const { definition } = message.messageBody.messageBody as WeeklyStreakProgressCompletionMessageBody;
  const targetDaysArray = extractTargetDaysFromLearnerGoals(learnerGoals);

  const weeklyStreakProgressData: WeeklyStreakProgressToastData = {
    targetDays: targetDaysArray,
    completedDays: convertApiDaysToGoalSettingDays(definition.completedDays),
    weeklyTarget: targetDaysArray.length > 0 ? targetDaysArray.length : WEEKLY_STREAKS_DEFAULT_TARGET_DAYS,
  };

  setWeeklyStreakProgressToastData(weeklyStreakProgressData);
};

/**
 * Update daily goal with progress from Learning Assistant message
 * Handles different goal types generically
 * @param targetGoal The goal to update
 * @param progressMessage The progress message from Learning Assistant
 * @returns Updated daily goal and whether the goal was just completed
 */
export const updateDailyGoalWithProgress = (
  targetGoal: DailyGoal,
  progressMessage: DailyGoalProgressMessage
): { updatedGoal: DailyGoal; wasJustCompleted: boolean } => {
  const oldStatus = targetGoal.progressStatus;
  const newStatus = mapProgressStatus(progressMessage.progressStatus);
  const wasJustCompleted = oldStatus !== ProgressStatus.Completed && newStatus === ProgressStatus.Completed;

  const goalTypeName = targetGoal.goalTypeAndProgressDetails.__typename;

  if (goalTypeName && isCompleteItemsProgressDetails(targetGoal.goalTypeAndProgressDetails)) {
    // Extract the goal type from the typename (e.g., "CompleteItems" from "LearnerGoals_DailyGoalCompleteItems")
    const goalType = goalTypeName.replace('LearnerGoals_DailyGoal', '');

    // Construct the full LA message type
    const laMessageType = `org.coursera.learningassistance.messages.dailygoals.DailyGoalProgress${goalType}`;

    // Check if this message type exists in the progress data
    if (laMessageType in progressMessage.progress) {
      const progressData = progressMessage.progress[laMessageType as keyof DailyGoalProgressData] as
        | DailyGoalProgressCompleteItems
        | undefined;

      if (progressData) {
        // Add the __typename field to match GraphQL type
        const progressDetailsWithTypename = {
          ...progressData,
          __typename: 'LearnerGoals_CompleteItemsProgressDetails' as const,
        };

        // At this point TypeScript knows progressData has the CompleteItems structure
        return {
          updatedGoal: {
            ...targetGoal,
            progressStatus: newStatus,
            goalTypeAndProgressDetails: {
              ...targetGoal.goalTypeAndProgressDetails,
              progressDetails: progressDetailsWithTypename,
            },
          },
          wasJustCompleted,
        };
      }
    }
  }

  // For daily goal types that do not have specific progress handling or invalid progress data,
  // just update the progress status
  return {
    updatedGoal: {
      ...targetGoal,
      progressStatus: newStatus,
    },
    wasJustCompleted,
  };
};

/**
 * Type guard to ensure a string is a valid DailyGoalTypeNameString
 */
const isDailyGoalTypeNameString = (typename: string | undefined): typename is DailyGoalTypeNameString => {
  return typename !== undefined && Object.values(DailyGoalTypeNames).includes(typename as DailyGoalTypeNameString);
};

export type ProcessMessagesCallback = {
  setDailyGoals: React.Dispatch<React.SetStateAction<Array<DailyGoal> | undefined>>;
  setModuleCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistanceMessage | undefined>
  >;
  setCourseCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistanceMessage | undefined>
  >;
  pushToastId: (newToastId: DailyGoalTypeNameString) => void;
};

/**
 * Process daily goal progress messages specifically
 * @param message The daily goal progress message
 * @param dailyGoals Current daily goals array
 * @param setDailyGoals Function to update daily goals state
 * @param pushToastId Function to push a new toast ID
 */
const processDailyGoalProgressMessage = (
  message: LearningAssistantDailyGoalMessage,
  dailyGoals: Array<DailyGoal>,
  setDailyGoals: React.Dispatch<React.SetStateAction<Array<DailyGoal> | undefined>>,
  pushToastId: (newToastId: DailyGoalTypeNameString) => void
): void => {
  const goalType = extractGoalTypeFromMessageId(message.messageBody.id.messageTypeId);
  if (!goalType) return;

  const goalIndex = findIndexOfMatchingDailyGoal(goalType, dailyGoals);
  if (goalIndex === -1) return;

  const progressMessage = message.messageBody.messageBody.definition.dailyGoals[0];

  setDailyGoals((prevGoals) => {
    if (!prevGoals) return prevGoals;

    const updatedGoals = [...prevGoals];
    const targetGoal = updatedGoals[goalIndex];

    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const { updatedGoal, wasJustCompleted } = updateDailyGoalWithProgress(targetGoal, progressMessage);
    updatedGoals[goalIndex] = updatedGoal;

    // If the goal was just completed and we have a toast callback, update the toast ID
    if (wasJustCompleted && pushToastId) {
      const typename = updatedGoal.goalTypeAndProgressDetails?.__typename;
      if (isDailyGoalTypeNameString(typename)) {
        pushToastId(typename);
      }
    }

    return updatedGoals;
  });
};

const processModuleCompletionMessage = (
  message: LearningAssistanceMessage,
  moduleId: string | undefined,
  setModuleCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistanceMessage | undefined>
  >
): void => {
  // Extract module ID from messageTypeId format: "ModuleCompletion~lUNIsm93Ee6vVQ4BPfdaww!~uxux9"
  // We need the last part after the final tilde (~)
  // Checking if the current moduleId matches the moduleId in the message to handle the case where a learner navigates to a new module
  const extractedModuleId = message.messageBody.id.messageTypeId.split('~').pop();
  if (moduleId === extractedModuleId) {
    setModuleCompletionLearningAssistantMessage(message);
  }
};

const isValidModuleCompletionMessage = (message: LearningAssistanceMessage) => {
  return message.messageBody.messageBody.typeName === MODULE_COMPLETION_MESSAGE_TYPE;
};

const isValidCourseCompletionMessage = (message: LearningAssistanceMessage) => {
  return message.messageBody.messageBody.typeName === COURSE_COMPLETION_MESSAGE_TYPE;
};

const processCourseCompletionMessage = (
  message: LearningAssistanceMessage,
  setCourseCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistanceMessage | undefined>
  >
): void => {
  setCourseCompletionLearningAssistantMessage(message);
};

type ProcessLearningAssistantMessages = {
  messages: LearningAssistanceMessage[];
  currentGoals: Array<DailyGoal>;
  moduleId: string | undefined;
  learnerGoals: LearnerGoals | undefined | null;
  setDailyGoals: React.Dispatch<React.SetStateAction<Array<DailyGoal> | undefined>>;
  setModuleCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistanceMessage | undefined>
  >;
  setCourseCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistanceMessage | undefined>
  >;
  pushToastId: (newToastId: DailyGoalTypeNameString) => void;
  setWeeklyStreakCelebrationData?: React.Dispatch<React.SetStateAction<WeeklyStreakCelebrationData | undefined>>;
  setWeeklyStreakProgressToastData?: React.Dispatch<React.SetStateAction<WeeklyStreakProgressToastData | undefined>>;
};

/**
 * Process Learning Assistant messages and update daily goals
 * Handles multiple message types and routes them to appropriate processors
 * @param messages Array of messages from Learning Assistant
 * @param dailyGoals Current daily goals array
 * @param setDailyGoals Function to update daily goals state
 * @param setModuleCompletionLearningAssistantMessage Function to update module completion learning assistant message
 * @param setCourseCompletionLearningAssistantFunction to update course completion learning assistant message
 * @param pushToastId Function to push a new toast ID
 * @param setWeeklyStreakCelebrationData Optional function to update weekly streak view data
 */
export const processLearningAssistantMessages = ({
  messages,
  currentGoals,
  moduleId,
  learnerGoals,
  setDailyGoals,
  setModuleCompletionLearningAssistantMessage,
  setCourseCompletionLearningAssistantMessage,
  pushToastId,
  setWeeklyStreakCelebrationData,
  setWeeklyStreakProgressToastData,
}: ProcessLearningAssistantMessages): void => {
  if (!messages || messages.length === 0 || !currentGoals) {
    return;
  }

  // Only look at the most recent message
  const message = messages[0];

  // Validate that this is a daily goal progress message
  if (isValidDailyGoalProgressMessage(message)) {
    const dailyGoalMessage = message as LearningAssistantDailyGoalMessage;
    processDailyGoalProgressMessage(dailyGoalMessage, currentGoals, setDailyGoals, pushToastId);

    // Check if this is a learning item goal (the key in the progress object)
    const firstDailyGoal = dailyGoalMessage.messageBody.messageBody.definition.dailyGoals[0];
    if (firstDailyGoal?.progress) {
      const progressKeys = Object.keys(firstDailyGoal.progress);
      const hasLearningItemGoal = progressKeys.some((key) => isLearningItemDailyGoal(key));

      if (hasLearningItemGoal) {
        makeWeeklyStreaksImpression();
      }
    }
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  } else if (isValidModuleCompletionMessage(message)) {
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    processModuleCompletionMessage(message, moduleId, setModuleCompletionLearningAssistantMessage);
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  } else if (isValidCourseCompletionMessage(message)) {
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    processCourseCompletionMessage(message, setCourseCompletionLearningAssistantMessage);
  } else if (isValidWeeklyStreakProgressCompletionMessage(message)) {
    // Use the same LA message but determine which UI to show based on completion status
    const { definition } = message.messageBody.messageBody as WeeklyStreakProgressCompletionMessageBody;
    const targetDaysArray = extractTargetDaysFromLearnerGoals(learnerGoals);
    const weeklyTargetComplete = isWeeklyTargetComplete(targetDaysArray, definition.completedDays);

    if (weeklyTargetComplete && setWeeklyStreakCelebrationData) {
      // Show celebration modal when weekly target is complete
      processWeeklyStreakProgressCompletionMessage(message, setWeeklyStreakCelebrationData);
    } else if (!weeklyTargetComplete && setWeeklyStreakProgressToastData) {
      // Show progress toast when weekly target is not yet complete
      processWeeklyStreakProgressMessage(message, learnerGoals, setWeeklyStreakProgressToastData);
    }
  }
};
